export function removeFrom<T>(
  list: T[],
  predicate: (item: T) => boolean,
  replacement: T = null
): T[] {
  if (!list || !list.length) {
    return [];
  }
  const removed = list.filter(predicate);
  if (replacement) {
    removed.forEach((item) => list.splice(list.indexOf(item), 1, replacement));
  } else {
    removed.forEach((item) => list.splice(list.indexOf(item), 1));
  }
  return removed;
}

export function prependRemoved<T>(
  list: T[],
  predicate: (item: T) => boolean,
  sortRemoved?: (a: T, b: T) => number
): T[] {
  const removed = removeFrom(list, predicate);
  if (sortRemoved) {
    removed.sort(sortRemoved);
  }
  return removed.concat(list);
}

export function unique<T = any>(
  all: T[],
  filter: T[],
  equals: (a: T, b: T) => boolean
): T[] {
  return all.filter((v) => !filter.some((u) => equals(v, u)));
}

export function equals<T = any>(
  a: T[],
  b: T[],
  equals?: (a: T, b: T) => boolean
): boolean {
  equals = equals || ((a, b) => a === b);
  return a.length === b.length && a.every((v) => b.includes(v));
}

export const partition = <T>(
  arr: T[],
  predicate: (v: T, i?: number, ar?: T[]) => boolean
): [T[], T[]] =>
  (arr || []).reduce(
    (item, accumulator, index, array) => (
      item[+!predicate(accumulator, index, array)].push(accumulator), item
    ),
    [[], []]
  );

export function sortedByDate<T = any>(items: T[], dateFn: (a: T) => Date): T[] {
  if (!items?.length) {
    return [];
  }
  const list = items.slice();
  return list.sort((a, b) => dateFn(a).getTime() - dateFn(b).getTime());
}

export function sortedByDateLex<T = any>(
  items: T[],
  dateFn: (a: T) => string,
  inPlace = true
): T[] {
  if (!items?.length) {
    return items;
  }
  const list = inPlace ? items : items.slice();
  const fn = (a: T) => dateFn(a) || '';
  return list.sort((a, b) => fn(a).localeCompare(fn(b)));
}

export function sortedByBoolean<T = any>(
  items: T[],
  boolFn: (a: T) => boolean,
  inPlace = true
): T[] {
  if (!items?.length) {
    return items;
  }
  const list = inPlace ? items : items.slice();
  const fn = (a: T) => boolFn(a);
  return list.sort((a, b) => {
    const c = fn(a);
    const d = fn(b);
    return c === d ? 0 : c ? -1 : 1;
  });
}

export function split<T = any>(items: T[], size: number): T[][] {
  const result = [];
  const n = items.length;

  let i = 0;
  while (i < n) {
    result.push(items.slice(i, i + size));
    i += size;
  }

  return result;
}

export function splitEntries(
  obj: object,
  n: number,
  keyFormat: (key: string) => string
): any[][] {
  if (!obj) {
    return [];
  }
  let entries = Object.entries(obj);
  entries = keyFormat ? entries.map(([k, v]) => [keyFormat(k), v]) : entries;
  return split(entries, n);
}

export function filterById<T = any>(a: T[], id: (a: T) => string): T[] {
  if (!a?.length) {
    return [];
  }
  const filter = {};
  a.forEach((v) => (filter[id(v)] = v));

  return Object.values(filter);
}

export function groupById<T = any>(
  a: T[],
  id: (a: T) => string
): Record<string, T[]> {
  if (!a?.length) {
    return {};
  }
  const groups = {};
  a.forEach((v: T) => {
    const key = id(v);
    const list = groups[key] || [];
    list.push(v);
    groups[id(v)] = v;
  });

  return groups;
}

export function toArray<T = any>(a: T | T[]): T[] {
  return Array.isArray(a) ? a : [a];
}